import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { STRAPI_BASE_URL } from "../../../api/baseURL";
import userin from "../../../assets/images/account_circle.svg";
import faprofile from "../../../assets/images/account_circlegray.svg";
import headerbackarrow from "../../../assets/images/back-arrow-left.svg";
import closeicon from "../../../assets/images/close.svg";
import faBook from "../../../assets/images/dynamic_feed.svg";
import faGlobe from "../../../assets/images/language.svg";
import logo from "../../../assets/images/Logo.svg";
import logoutIcon from "../../../assets/images/logout.svg";
import faExclamationTriangle from "../../../assets/images/report_problem.svg";
import faCog from "../../../assets/images/settings.svg";
import smLogo from "../../../assets/images/sm-logo.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useScreenResolution from "../../../hooks/useScreenResolution";
import { selectCommonApiContent } from "../../../redux/features/commonApiDataSlice";
import { selectLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import { selectProfileData } from "../../../redux/features/profileSlice";
import { selectUtilsWhole } from "../../../redux/features/toolsSlice";
import NotificationBell from "../../../pages/Notifications/NotificationBell";
import Dropdown from "../dropdown/dropdown";
import Stepper from "../stepper/stepper";
import {
  claimPaths,
  emailOrMobileChangePaths,
  lifeInsuranceOverviewPaths,
  motorInsurancePaths,
  navigateToHomePaths,
  policyOverviewPaths,
} from "./data";
import "./Header.css";

const settingsMenuItems = [
  {
    showIfIsLoggedIn: true,
    icon: faprofile,
    text_en: "Profile Settings",
    text_ar: "إعدادات الملف الشخصي",
    link: "/profile-settings",
    is_internal: true,
  },
  {
    icon: faGlobe,
    text_en: "Switch to Arabic (العربية)",
    text_ar: "Switch to English",
    link: "",
    handleLangChng: true,
  },
  {
    icon: faBook,
    text_en: "Learning Center",
    text_ar: "مركز التعلم",
    hide: true,
  },
  {
    icon: faExclamationTriangle,
    text_en: "Report a bug",
    text_ar: "الإبلاغ عن مشكلة",
    is_internal: false,
    link: "https://forms.cloud.microsoft/r/FamLUe2dUi",
  },
];

const Header = ({
  className,
  invalidPage = false,
  isPopup = false,
  onCloseModal,
  showBackBtn,
  backClickHandler,
  showStepper,
  activeStep,
  steps,
  isWideStepper = false,
  /**
   * Suppresses the mobile close in the top-right. The policy journeys put
   * their own close in the top-left, in the same bar as the back button, and
   * showing both left two X's on screen — one in each corner. Flows that have
   * no journey layout of their own (sign-in, sign-up, Essential Care, Express
   * Shield) leave this alone and keep the header's.
   */
  hideMobileClose = false,
}) => {
  const navigate = useNavigate();
  const screenResolution = useScreenResolution();
  const translate = useLanguageTranslate();

  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  const headerReducerData = useSelector(selectCommonApiContent);
  const headerAttributes = useMemo(() => {
    if (!headerReducerData) return null;

    // Case 1: Strapi v4-style data.attributes
    if (headerReducerData.data?.attributes) {
      return headerReducerData.data.attributes;
    }

    // ✅ Case 2: Your current response: { data: { ...fields }, meta: {} }
    if (headerReducerData.data && !headerReducerData.data.attributes) {
      return headerReducerData.data;
    }

    // Case 3: Slice already stored attributes directly
    if (headerReducerData.attributes) {
      return headerReducerData.attributes;
    }

    // Fallback: assume the object itself is attributes
    return headerReducerData;
  }, [headerReducerData]);

  const headerData = useMemo(() => {
    // Base items (static ones)
    const baseItems = [...settingsMenuItems];

    // If we don't have API data yet, still show logout item
    if (!headerAttributes) {
      return [
        ...baseItems,
        {
          showIfIsLoggedIn: true,
          icon: logoutIcon,
          text_en: "Logout",
          text_ar: "تسجيل الخروج",
          type: "logout",
        },
      ];
    }

    const dynamicKeys = [
      "settings_t_and_conditions",
      "settings_privacy_and_security",
      "settings_fees_and_charges",
    ];
    const isArabic = (localStorage.getItem("lang") || "en") === "ar";

    const dynamicItems = dynamicKeys
      .map((key) => {
        const section = headerAttributes[key];
        if (!section || !section.icon) return null;

        const iconObj = section.icon;
        const media =
          isArabic && iconObj.arabic_img ? iconObj.arabic_img : iconObj.img;
        const iconUrl = media?.url ? `${STRAPI_BASE_URL}${media.url}` : null;

        // If later you add `link` field on these entries, it will be used automatically
        const linkObj = section.link || {};

        return {
          icon: iconUrl, // ✅ match static items: a string path for <img src={item.icon} />
          text_en: linkObj.label_en || iconObj.img_alt_en || "",
          text_ar: linkObj.label_ar || iconObj.img_alt_ar || "",
          is_internal: linkObj.is_internal ?? false,
          link: linkObj.link || "",
          hide: linkObj.hide ?? false,
        };
      })
      .filter(Boolean);

    return [
      ...baseItems,
      ...dynamicItems,
      {
        showIfIsLoggedIn: true,
        icon: logoutIcon,
        text_en: "Logout",
        text_ar: "تسجيل الخروج",
        type: "logout",
      },
    ];
  }, [headerAttributes]);

  const toggleSettingsMenu = () => {
    setShowSettingsMenu((s) => !s);
  };

  const loginTokenInfo = useSelector(selectLoginTokenInfo);

  const { pathname, search } = useLocation();

  const optionalcloseClickHanlder = () => {
    if (pathname?.includes("/new-motor-policy")) {
      navigate("/new-policy");
    } else if (navigateToHomePaths.some((path) => pathname?.includes(path))) {
      navigate("/");
    } else if (
      emailOrMobileChangePaths.some((path) => pathname?.includes(path))
    ) {
      navigate("/profile-settings");
    } else if (policyOverviewPaths.some((path) => pathname?.includes(path))) {
      navigate(-1);
    } else if (claimPaths.some((path) => pathname?.includes(path))) {
      navigate("/file-a-claim");
    } else if (
      lifeInsuranceOverviewPaths.some((path) => pathname?.includes(path))
    ) {
      const qs = search
        ? `${search}&tabType=ManagePolicy`
        : `?tabType=ManagePolicy`;
      navigate(`/life-insurance/policy-overview${qs}`);
    } else if (motorInsurancePaths.some((path) => pathname?.includes(path))) {
      navigate(-1);
    } else if (pathname?.includes("/express-shield")) {
      navigate("/new-policy");
    } else {
      goToHome();
    }
  };

  const goToHome = () => navigate("/");

  const sidenavOpen = useSelector(selectUtilsWhole).sidenav_open;
  const profileData = useSelector(selectProfileData);

  // Handlers passed to Dropdown so it can run actions inline
  const handleLangToggle = () => {
    const current = localStorage.getItem("lang") || "en";
    const next = current === "ar" ? "en" : "ar";
    localStorage.setItem("lang", next);
    window.location.reload();
  };

  return (
    <>
      <nav
        className={`fixed z-20 bg-white top-0 h-[48px] max767:border-none ${
          className || ""
        } inset-x-0 bg-white py-0.5 px-0 text-black font-medium flex justify-between items-center max767:pt-[22px] max767:pb-[0px] max767:px-0  max767:min-h-0 max767:h-14 border-b-[#E5E9EB] border-b border-solid max767:border-none`}
      >
        <div className=" rtl:mr-[30px] flex justify-center items-center bg-white ipad:w-[115px] ipad:h-7 ipad:pl-2 ipad:ml-2 max767:hidden ipad:w-auto">
          {showBackBtn && (
            <button
              className="bg-navy-blue py-2 rounded mt-32 left-6 rtl:right-4 absolute flex"
              onClick={backClickHandler}
            >
              <img
                src={headerbackarrow}
                alt="back arrow"
                className="mr-2.5 rtl:ml-[5px] rtl:rotate-[180deg]"
              />
              <span className="back-text inline max767:hidden">
                {translate("Back", "رجوع")}
              </span>
            </button>
          )}
          {!screenResolution.mobile && (
            <img
              onClick={goToHome}
              src={sidenavOpen ? logo : smLogo}
              alt="logo"
              className={
                sidenavOpen
                  ? "logofull w-[135px] h-9 ml-[30px] mt-[-5px]"
                  : "smLogo ml-3"
              }
            />
          )}
          {/* The rail's collapse control lives in the rail itself, not here —
              see sideNavbar.js. The header still reads `sidenav_open` because
              the logo swaps to its compact mark when the rail narrows. */}
        </div>

        {!isPopup ? (
          <div className="flex items-center space-x-4 rtl:space-x-reverse mr-6 ml-6 relative max767:w-full max767:justify-between max767:m-0 max767:pl-5 max767:pr-5 max767:pb-0 max767:shadow-none max767:border-0 rtl:mr-0 rtl:ml-6 max767:rtl:ml-0">
            {!loginTokenInfo && (
              <button
                className={`${
                  screenResolution.mobile
                    ? "isMobile ml-0 text-xl not-italic font-medium"
                    : "notIsMobile"
                } btn-sign-in bg-[#55C6EF] mt-[-1px] rounded-md inline-flex flex items-center gap-2.5 text-[#48535B] text-center text-sm not-italic font-semibold leading-6 tracking-[-0.084px] px-3 py-1 max767:text-sm`}
                onClick={() => navigate("/sign-in")}
              >
                <img src={userin} alt="" className="max767:block hidden" />
                {translate("Sign in", "تسجيل الدخول")}
              </button>
            )}

            {loginTokenInfo && screenResolution.mobile && (
              <h4
                className={`${
                  screenResolution.mobile
                    ? "isMobile ml-6 text-xl not-italic font-medium"
                    : "notIsMobile"
                }`}
              >
                {translate("Welcome", "مرحبًا")}{" "}
                {profileData?.FirstName?.split(" ")?.length
                  ? `${profileData?.FirstName?.split(" ")[0] || ""} `
                  : "User"}
              </h4>
            )}

            <div className="flex gap-2 items-center">
              {/* Notifications. Signed-in only — the inbox is derived from the
                  JWT, so there is nothing to show a logged-out visitor.
                  NotificationBell owns its own trigger, badge and responsive
                  shell (popover on desktop, bottom sheet on mobile). */}
              {loginTokenInfo && <NotificationBell />}

              {!screenResolution.mobile && (
                <div className="relative pt-3">
                  <button
                    className="focus:outline-none"
                    onClick={toggleSettingsMenu}
                  >
                    <img
                      src={!profileData ? faCog : userin}
                      alt=""
                      className="mr-2 mb-1 rtl:mr-0 rtl:ml-2"
                    />
                  </button>
                  {showSettingsMenu && (
                    <Dropdown
                      items={headerData}
                      closeDropdown={() => setShowSettingsMenu(false)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {screenResolution.mobile && !hideMobileClose && (
              <div className="header-mobile-close w-full flex justify-end px-4 py-2">
                <button
                  type="button"
                  aria-label={translate("Close", "إغلاق")}
                  className="p-2"
                  onClick={
                    onCloseModal ? onCloseModal : optionalcloseClickHanlder
                  }
                >
                  <img src={closeicon} alt="Close Icon" className="w-6 h-6" />
                </button>
              </div>
            )}
            {showStepper && (
              <Stepper
                activeStep={activeStep}
                steps={steps}
                isWide={isWideStepper}
              />
            )}
            <div
              className={`flex items-center justify-center ${
                invalidPage ? "hideclose" : ""
              }`}
            >
              <button
                className="bpy-2 mr-10 px-0 py-[15px] rtl:ml-10 rounded flex right-[26px] top-0 text-black text-sm not-italic font-semibold leading-6 tracking-[-0.084px] max767:hidden"
                onClick={
                  onCloseModal ? onCloseModal : optionalcloseClickHanlder
                }
              >
                <img src={closeicon} alt="Close Icon" className="mr-2" />
                {translate("Close", "إغلاق")}
              </button>
            </div>
          </>
        )}
      </nav>
      <div className="placeholder h-[0px] max"></div>
    </>
  );
};

export default Header;
